import type Owner from "@ember/owner";
import type RouterDSL from "@ember/routing/-private/router-dsl";
import { service } from "@ember/service";
import EmberRouter from "@embroider/router";
import { isTesting } from "@embroider/macros";
import { setupRouter } from "ember-app-scheduler";
import { properLinks } from "ember-primitives/proper-links";
import bloodPressureRoutes from "parog-web/blood-pressure/router/blood-pressure-routes";
import compoundSurveyRoutes from "parog-web/compound-survey/router/compound-survey-routes";
import ENV from "parog-web/config/environment";

// @ts-expect-error due to a patch made on `should-handle-link` to handle regex among literal strings, will be fixed when upgrading the package.
@properLinks({
  ignore: [/^\/s\//, /\/api\/v1\/\w+/]
})
export default class Router extends EmberRouter {
  @service router;

  override location = ENV.locationType;
  override rootURL = ENV.rootURL;

  constructor(owner: Owner) {
    super(owner);

    setupRouter(this.router);
  }
}

Router.map(function () {
  this.route("login", function () {
    this.route("aphp-medics");
    this.route("hcl-medics");
    this.route("pro-sante-connect");
    this.route("medic-password", { path: "medic" });
    this.route("medic-password-forgotten", {
      path: "medic/forgot-password/send-instructions"
    });
    this.route("patient-link", { path: "patient" });
    this.route("patient-password", {
      path: "patient/with-account"
    });
    this.route("patient-password-forgotten", {
      path: "patient/forgot-password/send-instructions"
    });
  });

  this.route("terms-of-use");
  this.route("view-terms-of-use");
  this.route("openid-redirect");
  this.route("patient-web", function () {
    this.route("login");
    this.route("logout");
    this.route("login-with-account");
    this.route("forgot-password", function () {
      this.route("send-instructions");
      this.route("set-password");
    });
    this.route("protected", { path: "/" }, function () {
      this.route("prescriptions", function () {
        this.route("paramedical-care-display", {
          path: "/paramedical-care-display/:source_je_id"
        });
        this.route("medical-imaging-display", {
          path: "/medical-imaging-display/:source_je_id"
        });
        this.route("bio-exam-display", {
          path: "/bio-exam-display/:source_je_id"
        });
        this.route("medication-display", {
          path: "/medication-display/:source_je_id"
        });
        this.route("tls-display", {
          path: "/tls-display/:source_je_id"
        });
        this.route("generic-display", {
          path: "/generic-display/:source_je_id"
        });
      });
      this.route("documents", function () {
        this.route("consultation-report-display", {
          path: "/consultation-report-display/:source_je_id"
        });
        this.route("default-display", {
          path: "/default-display/:source_je_id"
        });
      });
      this.route("online-consultation");

      this.route("bio-results", function () {
        this.route("edition");
        this.route("summary");
      });

      this.route("dashboard", function () {
        this.route("declare");
        this.route("consult");
        this.route("learn");
        this.route("article", { path: "article/:id" });
        this.route("living-donor", function () {
          this.route("information");
          this.route("patient-pathway");
          this.route("relatives");
        });
      });

      this.route(
        "form-requested",
        { path: "forms-requested/:form_requested_id" },
        function () {
          this.route("feature", { path: "/:feature" }, function () {
            this.route("component", { path: "/:component" });
          });
        }
      );

      bloodPressureRoutes.call(this);
      compoundSurveyRoutes.call(this);
      resultsTableRoutes.call(this);

      this.route("hemodialysis", function () {
        this.route("sessions", function () {
          this.route("session", { path: "/:session_id" });
          this.route("new");
        });
      });

      this.route("dp", function () {
        this.route("sessions", function () {
          this.route("session", { path: "/:session_id" });
          this.route("new");
        });
      });

      this.route("chat");
      this.route("consult", function () {
        this.route("medical-synthesis");
        this.route("bio-exams");
        this.route("prescriptions");
        this.route("bio-constants");
      });

      this.route("therapeutic-assistance", {
        path: "therapeutic-assistance/:source_je_id"
      });

      // FORC
      this.route("feature", { path: "/:feature" }, function () {
        this.route("component", { path: "/:component" }, function () {
          this.route("edition", { path: "/:source_je_id" });
        });
      });

      this.route("hla", function () {
        this.route("consult", { path: "consult/:je_id" });
      });
    });
  });

  this.route("medics-web", function () {
    this.route("login");
    this.route("logout");
    this.route("forgot-password", function () {
      this.route("send-instructions");
      this.route("set-password");
    });

    this.route("protected", { path: "/" }, function () {
      this.route("index", { path: "/" }, function () {
        this.route("alerts-and-notifications");
        this.route("tasks-and-consultations");
        this.route("patient-list");

        this.route("dmp-bulk-download");

        this.route("bio-exams", function () {
          this.route("summary");
          this.route("matchlab-review", { path: "matchlab-review/:je_id" });
        });
      });

      this.route("help", function () {
        this.route("dmp");
      });

      this.route("profile");

      this.route("prescriptions");

      this.route("mrc-stage-evaluation");

      this.route("find-patient", { path: "find-patient/:concatenated_ipp" });

      this.route("patients", function () {
        this.route("new");
        this.route("import");

        this.route("patient", { path: ":patient_id" }, function () {
          this.route("dashboard", function () {
            this.route("main", { path: "/" }, function () {
              // FORC as a modal
              this.route("feature", { path: "/:feature" }, function () {
                this.route("component", { path: "/:component" }, function () {
                  this.route("edition", { path: "/:source_je_id" });
                });
              });

              this.route("kfre-five-years-edition-form", {
                path: "/kfre/five-years-edition-form"
              });
              this.route("kfre-two-years-edition-form", {
                path: "/kfre/two-years-edition-form"
              });

              resultsTableRoutes.call(this);
            });

            this.route("consultation-report", function () {
              this.route("new");
              this.route("edit", { path: "edit/:consultation_id" });
            });

            this.route("generic-medical-data");
            this.route("medical-history", function () {
              this.route("desease-event", function () {
                this.route("edition", { path: "/:source_je_id" });
                this.route("new");
              });
            });
            this.route("edit");

            this.route("bio-results", function () {
              this.route("summary");
            });
          });
          this.route("healthcare-plan");
          this.route("prescriptions", function () {
            this.route("medical-imaging-display", {
              path: "/medical-imaging-display/:source_je_id"
            });
            this.route("medication-display", {
              path: "/medication-display/:source_je_id"
            });
            this.route("paramedical-care-display", {
              path: "/paramedical-care-display/:source_je_id"
            });
            this.route("bio-exam-display", {
              path: "/bio-exam-display/:source_je_id"
            });
            this.route("tls-display", {
              path: "/tls-display/:source_je_id"
            });
            this.route("generic-display", {
              path: "/generic-display/:source_je_id"
            });
          });

          bloodPressureRoutes.call(this);
          compoundSurveyRoutes.call(this);

          this.route("hemodialysis", function () {
            this.route("sessions", function () {
              this.route("session", { path: "/:session_id" });
              this.route("new");
            });
          });

          this.route("dp", function () {
            this.route("sessions", function () {
              this.route("session", { path: "/:session_id" });
              this.route("new");
            });
          });

          this.route("bio-prescription", function () {
            this.route("template-selection");
            this.route("edition-form");
          });

          this.route("medication-prescription", function () {
            this.route("edition-form");
          });

          this.route("bio-results", function () {
            this.route("edition");
          });

          this.route("timeline");
          this.route("administrative", function () {
            this.route("identity-access-logs");
            this.route("disenrollment");
          });
          this.route("documentation-page");
          this.route("edit");

          this.route("threshold-triggers");
          this.route("reimbursement");
          this.route("consultation", function () {
            this.route("report-form");
            this.route("patient-pathway");
          });
          this.route("therapeutic-assistance", {
            path: "therapeutic-assistance/:source_je_id"
          });
          this.route("education", function () {
            this.route("learn");
            this.route("article", { path: "article/:id" });
            this.route("living-donor", function () {
              this.route("information");
              this.route("patient-pathway");
              this.route("relatives");
            });
          });

          this.route("medics");

          this.route(
            "form-requested",
            { path: "forms-requested/:form_requested_id" },
            function () {
              this.route("feature", { path: "/:feature" }, function () {
                this.route("component", { path: "/:component" });
              });
            }
          );

          // DMP routes
          this.route("dmp", function () {
            this.route("documents", function () {
              // eslint-disable-next-line ember/routes-segments-snake-case
              this.route("show", { path: ":uniqueId" });
            });
          });

          // FORC
          this.route("enrollment");
          this.route("feature", { path: "/:feature" }, function () {
            this.route("component", { path: "/:component" }, function () {
              this.route("edition", { path: "/:source_je_id" });
            });
          });

          this.route("hla", function () {
            this.route("consult", { path: "consult/:je_id" });
          });

          this.route("health-centre");

          this.route("document-display", {
            path: "/document-display/:source_je_id"
          });
          this.route("ordoclic");

          this.route("consultation-report", function () {
            this.route("edition-form", {
              path: "edition-form/:consultation_id"
            });
          });
          this.route("configure");
        });
      });

      this.route("medics", function () {
        this.route("new");

        this.route("medic", { path: ":medic_id" });
      });

      this.route("global-medical-synthesis", function () {
        this.route("download");
      });

      this.route("health-centre-invoices");

      this.route("article", { path: "article/:id" });

      this.route("toucan-dashboard");
    });
  });

  if (isTesting()) {
    this.route("test-routes", function () {
      this.route("formy");
    });
  }

  // Catches unknown route.
  // @see https://guides.emberjs.com/release/routing/defining-your-routes/#toc_wildcard--globbing-routes
  this.route("not-found", { path: "*" });
});

function resultsTableRoutes(this: RouterDSL) {
  this.route("results-table", function () {
    this.route("temperature");
    this.route("measurements");
    this.route("capillarity-blood-glucose");
    this.route("vems");
    this.route("respiratory-rate");
    this.route("oxygene-saturation");
    this.route("adverse-event");
    this.route("feeling-evas");
    this.route("medical-event");
    this.route("scale-surveys");
    this.route("diabguide-recommendation");
    this.route("rch-survey");
    this.route("crohn-survey");
    this.route("stools-number");
    this.route("ibd-disk-survey");
  });
}
